import React, { useState, useEffect } from 'react';
import { MapPin, Calendar, CurrencyInr } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { supabase } from '@/lib/supabaseClient';
import TopBar from '@/components/layout/TopBar';
import SiteHeader from '@/components/layout/SiteHeader';
import BrandDivider from '@/components/brand/BrandDivider';

const PackagesPage = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    name: '',
    email: '',
    phone: '',
    travel_date: '',
    message: ''
  });

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const { data, error } = await supabase
        .from('tour_packages')
        .select('*')
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      setPackages(data || []);
    } catch (error) {
      toast.error('Failed to load packages');
      console.error('Error fetching packages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBookPackage = (pkg) => {
    setSelectedPackage(pkg);
    setShowBookingDialog(true);
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    setBookingLoading(true);

    try {
      const now = new Date().toISOString();
      const { error } = await supabase.from('inquiries').insert({
        id: crypto.randomUUID(),
        inquiry_type: 'tour_package',
        package_id: selectedPackage.id,
        package_name: selectedPackage.name,
        name: bookingForm.name,
        email: bookingForm.email,
        phone: bookingForm.phone,
        travel_date: bookingForm.travel_date,
        message: bookingForm.message || null,
        status: 'pending',
        created_at: now,
        updated_at: now,
      });
      if (error) throw error;
      toast.success('Package inquiry submitted successfully! We will contact you soon.');
      setShowBookingDialog(false);
      setBookingForm({
        name: '',
        email: '',
        phone: '',
        travel_date: '',
        message: ''
      });
    } catch (error) {
      toast.error('Failed to submit inquiry. Please try again.');
      console.error('Error submitting inquiry:', error);
    } finally {
      setBookingLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <TopBar />
      <SiteHeader showNav={false} showAdmin={false} backTo="/" backLabel="Back to Home" />

      {/* Hero */}
      <div className="bg-atithi-navy py-16 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <BrandDivider className="mb-6" />
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-light text-white mb-4 font-display" data-testid="packages-heading">
            Gujarat Tour Packages
          </h1>
          <p className="text-white/80 leading-relaxed">Explore the cultural richness and natural beauty of Gujarat</p>
        </div>
      </div>

      {/* Packages Grid */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {loading ? (
          <div className="text-center py-12">
            <p className="text-atithi-muted">Loading packages...</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="packages-grid">
            {packages.map((pkg, index) => (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="overflow-hidden border-atithi-border hover:shadow-xl transition-shadow duration-300 h-full flex flex-col" data-testid={`package-card-${pkg.id}`}>
                  <div className="h-48 overflow-hidden">
                    <img 
                      src={pkg.image_url || 'https://images.unsplash.com/photo-1566552881560-0be862a7c445?w=800'} 
                      alt={pkg.name}
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <CardHeader>
                    <CardTitle className="text-xl font-medium font-display text-atithi-navy">{pkg.name}</CardTitle>
                    <CardDescription className="flex items-center gap-2 text-atithi-muted">
                      <MapPin size={16} />
                      {pkg.destination}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex-1">
                    <p className="text-atithi-muted mb-4 leading-relaxed">{pkg.description}</p>
                    <div className="flex items-center gap-4 mb-3">
                      <Badge variant="secondary" className="flex items-center gap-1">
                        <Calendar size={14} />
                        {pkg.duration_days} {pkg.duration_days === 1 ? 'Day' : 'Days'}
                      </Badge>
                      <Badge variant="secondary" className="flex items-center gap-1 bg-[#10B981]/10 text-[#10B981]">
                        <CurrencyInr size={14} />
                        {pkg.price_per_person.toLocaleString()} / person
                      </Badge>
                    </div>
                    {pkg.highlights && (
                      <div className="mt-3">
                        <p className="text-sm font-medium text-atithi-navy mb-2">Highlights:</p>
                        <div className="flex flex-wrap gap-2">
                          {JSON.parse(pkg.highlights).slice(0, 3).map((highlight, i) => (
                            <span key={i} className="text-xs px-2 py-1 bg-[#F8F9FA] text-atithi-muted rounded">
                              {highlight}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                  <CardFooter>
                    <Button
                      onClick={() => handleBookPackage(pkg)}
                      className="w-full bg-atithi-navy hover:bg-atithi-navy-hover text-white rounded-xl"
                      data-testid={`book-package-button-${pkg.id}`}
                    >
                      Book Now
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Booking Dialog */}
      <Dialog open={showBookingDialog} onOpenChange={setShowBookingDialog}>
        <DialogContent className="sm:max-w-md" data-testid="booking-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Book Package</DialogTitle>
            <DialogDescription>
              {selectedPackage?.name} - {selectedPackage?.destination}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleBookingSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                required
                value={bookingForm.name}
                onChange={(e) => setBookingForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter your name"
                data-testid="dialog-name-input"
              />
            </div>
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                required
                value={bookingForm.email}
                onChange={(e) => setBookingForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="your@email.com"
                data-testid="dialog-email-input"
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                type="tel"
                required
                value={bookingForm.phone}
                onChange={(e) => setBookingForm(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+91 XXXXXXXXXX"
                data-testid="dialog-phone-input"
              />
            </div>
            <div>
              <Label htmlFor="travel_date">Preferred Travel Date *</Label>
              <Input
                id="travel_date"
                type="date"
                required
                value={bookingForm.travel_date}
                onChange={(e) => setBookingForm(prev => ({ ...prev, travel_date: e.target.value }))}
                data-testid="dialog-travel-date-input"
              />
            </div>
            <div>
              <Label htmlFor="message">Message (Optional)</Label>
              <Input
                id="message"
                value={bookingForm.message}
                onChange={(e) => setBookingForm(prev => ({ ...prev, message: e.target.value }))}
                placeholder="Any special requests..."
                data-testid="dialog-message-input"
              />
            </div>
            <Button
              type="submit"
              disabled={bookingLoading}
              className="w-full bg-atithi-navy hover:bg-atithi-navy-hover"
              data-testid="dialog-submit-button"
            >
              {bookingLoading ? 'Submitting...' : 'Submit Inquiry'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PackagesPage;
